<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConnectionState } from '@/services/connectionState';

const state = useConnectionState();
const visible = computed(() => state.value === 'reconnecting');

const { t } = useI18n({ useScope: 'global' });
</script>

<template>
  <Transition name="reconnecting">
    <div
      v-if="visible"
      class="reconnecting-banner"
      role="status"
      aria-live="polite"
      data-testid="reconnecting-banner"
    >
      <span class="spinner" aria-hidden="true" />
      <span class="text">{{ t('system.reconnecting') }}</span>
    </div>
  </Transition>
</template>

<style scoped>
.reconnecting-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 64px;
  gap: 16px;
  padding: 18px min(var(--entr-side), 6vw);
  background: color-mix(in srgb, var(--entr-ink) 92%, transparent);
  color: var(--entr-text);
  font-family: var(--entr-font-body);
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  letter-spacing: 0.01em;
  pointer-events: none;
  border-bottom: 2px solid color-mix(in srgb, var(--entr-logo-green) 70%, transparent);
  box-shadow: 0 4px 20px color-mix(in srgb, var(--entr-ink) 55%, transparent);
  backdrop-filter: blur(6px);
}

.text {
  overflow-wrap: anywhere;
}

.spinner {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border: 3px solid color-mix(in srgb, var(--entr-text) 28%, transparent);
  border-top-color: var(--entr-logo-green);
  border-radius: 50%;
  animation: reconnecting-spin 0.9s linear infinite;
}

@keyframes reconnecting-spin {
  to {
    transform: rotate(360deg);
  }
}

.reconnecting-enter-active,
.reconnecting-leave-active {
  transition:
    opacity 200ms ease,
    transform 200ms ease;
}

.reconnecting-enter-from,
.reconnecting-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
